/**
 * The frame of one dashboard list (PRD-0075 R-D2): a titled card, a link to where the list is in
 * full, the rows or what it says when there are none, and how many of how many it shows.
 */
import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { cn } from '@/utils/cn'

/** Rows a list shows before it points to the page that has them all. */
export const PANEL_ROWS = 6

export interface DashboardPanelRows {
  /** Rows in all, and how many of them the panel shows. */
  total: number
  shown: number
  /** What the list says when it has none. */
  empty: string
}

export interface DashboardPanelProps {
  heading: { id: string; icon: LucideIcon; title: string }
  /** Where the list is in full. */
  link: { href: string; label: string } | null
  rows: DashboardPanelRows
  children: ReactNode
  className?: string
}

/** The first rows of `items` a panel shows, and its counts. */
export function panelRows<T>(items: readonly T[], empty: string, limit = PANEL_ROWS) {
  const shown = items.slice(0, limit)
  return { shown, rows: { total: items.length, shown: shown.length, empty } }
}

export function DashboardPanel({ heading, link, rows, children, className }: DashboardPanelProps) {
  const { t } = useTranslation()
  const Icon = heading.icon
  return (
    <Card asChild className={cn('flex flex-col gap-3 rounded-xl p-5', className)}>
      <section aria-labelledby={heading.id}>
        <div className="flex items-center gap-2">
          <Icon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
          <h3 id={heading.id} className="min-w-0 flex-1 text-sm font-semibold text-foreground">
            {heading.title}
          </h3>
          {link ? (
            <Link
              to={link.href}
              className="shrink-0 text-xs text-primary underline-offset-2 hover:underline"
            >
              {link.label}
            </Link>
          ) : null}
        </div>
        {rows.total > 0 ? children : <p className="text-sm text-muted-foreground">{rows.empty}</p>}
        {rows.total > rows.shown ? (
          <p className="text-xs text-muted-foreground">
            {t('caseDashboard.panels.showing', { shown: rows.shown, total: rows.total })}
          </p>
        ) : null}
      </section>
    </Card>
  )
}
